import { ChangeDetectorRef, Component, effect, ElementRef } from '@angular/core';
import { PlayerService } from './library/player.service';
import { ControlService } from './library/control.service';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { UserVideoService } from './library/user-video.service';
import { DeviceSettingsService } from './library/device-settings.service';
import { RealtimeService } from './library/realtime/realtime.service';
import { CallFlagService } from 'src/app/services/learner/call-flag/call-flag.service';
import { TranslationService } from 'src/app/services/translation.service';
import { AppConstants } from 'src/app/app-constants';
import { filter, Subscription, take } from 'rxjs';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { AssemblyService } from './library/assembly/assembly.service';
import { CallStoreService } from './library/call-store/call-store.service';
import { GenderService } from 'src/app/services/general/gender.service';
import { getDeviceType } from 'src/app/utils/device/device.util';
import { ImagePreloaderService } from 'src/app/services/learner/image-preloader/image-preloader.service';
import { MediaStreamService } from './library/media-stream/media-stream.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { CertificationAcknowledgementService } from 'src/app/services/learner/certification-acknowledgement.service';
import { CameraStreamMonitorService } from 'src/app/services/learner/camera-stream-monitor.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { SentryService } from 'src/app/services/logger/sentry.service';

@Component({
  selector: 'app-call',
  standalone: false,
  templateUrl: './call.component.html',
  styleUrls: ['./call.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'call',
    },
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'settings',
    },
  ],
})
export class CallComponent {
  private observer: MutationObserver | undefined;
  callType: string = '';
  hasRequiredParams: boolean = false;
  private callFlagsSubscription!: Subscription;
  showGenderModal = false;
  isTranslationLoaded = false;
  private startCallSubscription?: Subscription;
  enrollmentStepUuid: string = '';
  enrollmentUuid: string = '';
  stepType: string = '';

  // Certification Video Acknowledgement
  showAcknowledgementModal = false;
  isCameraInactiveInterruption = false;
  showCameraInactiveWarning = false;
  private cameraMonitorSubscription?: Subscription;
  private certCameraCheckInterval: ReturnType<typeof setInterval> | null = null;
  private cameraPermissionStatus: PermissionStatus | null = null;
  private cameraMonitorCooldownUntil = 0;

  constructor(
    public playerService: PlayerService,
    public controlService: ControlService,
    public userVideoService: UserVideoService,
    public deviceSettingsService: DeviceSettingsService,
    public realtimeService: RealtimeService,
    public assemblyService: AssemblyService,
    public route: ActivatedRoute,
    public el: ElementRef,
    private cdr: ChangeDetectorRef,
    public callFlagService: CallFlagService,
    public callStore: CallStoreService,
    private translationService: TranslationService,
    private genderService: GenderService,
    private imagePreloaderService: ImagePreloaderService,
    private router: Router,
    private mediaStreamService: MediaStreamService,
    private logger: LoggerService,
    private certAckService: CertificationAcknowledgementService,
    private cameraMonitorService: CameraStreamMonitorService,
    private featureFlagsService: FeatureFlagsService,
    private sentryService: SentryService
  ) {
    // set services
    this.playerService.setControlService(controlService);

    this.controlService.setPlayerService(playerService);
    this.controlService.setUserVideoService(userVideoService);
    this.controlService.setDeviceSettingsService(deviceSettingsService);
    this.controlService.setRealtimeService(realtimeService);

    this.userVideoService.setControlService(controlService);
    this.userVideoService.setDeviceSettingsService(deviceSettingsService);
    this.userVideoService.setPlayerService(playerService);

    this.deviceSettingsService.setPlayerService(playerService);
    this.deviceSettingsService.setControlService(controlService);
    this.deviceSettingsService.setRealtimeService(realtimeService);

    this.realtimeService.setDeviceSettingsService(deviceSettingsService);
    this.realtimeService.setPlayerService(playerService);
    this.realtimeService.setControlService(controlService);

    effect(() => {
      if (
        this.callStore.getCertAcknowledgementPending()() &&
        this.callStore.getCurrentViewSignal()() === AppConstants.VIEW_PRE_CALL &&
        !this.showAcknowledgementModal
      ) {
        this.showAcknowledgementModal = true;
      }
    });
  }

  ngOnDestroy(): void {
    // Safety net: close any open call-session span when the /call route is destroyed.
    this.sentryService.endCallSession();
    this.sentryService.stopReplay();
    this.mediaStreamService.cleanupVideoStream();

    if (this.observer) {
      this.observer.disconnect();
      this.observer = undefined;
    }
    if (this.callFlagsSubscription) {
      this.callFlagsSubscription.unsubscribe();
    }
    this.playerService.stopAssembly();
    this.controlService.resetPlayer();
    if (this.startCallSubscription) {
      this.startCallSubscription.unsubscribe();
    }
    if (this.cameraMonitorSubscription) {
      this.cameraMonitorSubscription.unsubscribe();
    }
    this.cameraMonitorService.stopMonitoring();
    if (this.certCameraCheckInterval) {
      clearInterval(this.certCameraCheckInterval);
      this.certCameraCheckInterval = null;
    }
    if (this.cameraPermissionStatus) {
      this.cameraPermissionStatus.onchange = null;
      this.cameraPermissionStatus = null;
    }
  }

  ngOnInit() {
    this.sentryService.startReplay();
    this.callStore.setIsCallComplete(false);
    this.callStore.setIsVisualAidOpen(false);
    this.callStore.setHints('');
    this.callStore.setSkipConnectTranscriber(true);
    this.callStore.setHideCameraToggle(false);
    this.callStore.setAcknowledgementSessionToken(null);
    this.callStore.setCertAcknowledgementPending(false);
    this.imagePreloaderService.clearCache();

    this.translationService.loadScopedTranslation('call').then(() => {
      this.isTranslationLoaded = true;
      this.showGenderModal = this.genderService.isGenderRequiredForCurrentLanguage() &&
                         this.genderService.isGenderInputRequired();
    });

    this.translationService.loadScopedTranslation('settings');

    this.route.queryParams.subscribe(async (params) => {
      // For PATHWAY type, callType & callUuid  are optional
      const practiceType = params['practiceType'];
      // handle query params not existing
      if (!params['callUuid'] && practiceType !== 'PATHWAY') {
        this.controlService.goBackToAssigment();
        console.error('callUuid query param not found');
        return;
      }
      if (!params['moduleAssignmentUuid']) {
        this.controlService.goBackToAssigment();
        console.error('moduleAssignmentUuid query param not found');
        return;
      }

      if (!params['callType'] && practiceType !== 'PATHWAY') {
        this.controlService.goBackToAssigment();
        console.error('callType query param not found');
        return;
      }
      if (params['advance']) {
        this.controlService.advanceTypeQueryParam = params['advance'];
      }

      // set query params for the call
      const callUuid = params['callUuid'];
      const moduleAssignmentUuid = params['moduleAssignmentUuid'];
      this.callType = params['callType'] || '';
      const topClips = params['topClips'];
      this.enrollmentStepUuid = params['enrollmentStepUuid'];
      this.enrollmentUuid = params['enrollmentUuid'];
      this.stepType = params['stepType'];

      if (practiceType === 'PATHWAY') {
        this.hasRequiredParams = true;
        this.callStore.setModuleAssignmentUuid(moduleAssignmentUuid);
        this.callStore.setModuleAssignmentType(practiceType);
        return;
      }

      this.controlService.setQueryParams(
        moduleAssignmentUuid,
        callUuid,
        practiceType,
        this.callType,
        topClips
      );

      // Set global logging properties in CallStore
      const userUuid = localStorage.getItem('user_uuid') || '';
      this.callStore.setUserUuid(userUuid);
      this.callStore.setModuleAssignmentUuid(moduleAssignmentUuid);
      this.callStore.setModuleAssignmentType(practiceType);
      this.callStore.setCallUuid(callUuid);
      this.callStore.setCallType(this.callType);

      const hasPersonalizedPayload =
        !!this.callStore.getPersonalizedLaunchPayload();
      this.callStore.setIsPersonalizedCall(hasPersonalizedPayload);

      this.checkIfIsIpad();
      this.initCallFlags(callUuid);

    });

    if (
      this.controlService.isVisualAidCall &&
      this.controlService.callType === AppConstants.CALL_TYPE_GUIDED
    ) {
      this.controlService.isCaptionsOn = false;
    }
    this.detectBrowserBackButton();
  }

  ngAfterViewInit(): void {
    if (!this.hasRequiredParams) {
      this.controlService.goBackToAssigment();
      return;
    }
    // If gender selection modal is open, don't start call initialization yet
    if (this.showGenderModal) {
      return;
    }
    // Check if certification acknowledgement is required
    this.checkCertificationAcknowledgement();
  }

  private async checkCertificationAcknowledgement(): Promise<void> {
    const practiceType = this.callStore.getModuleAssignmentType();
    const callType = this.callStore.getCallType();

    if (practiceType !== AppConstants.TYPE_CERTIFICATION || callType !== AppConstants.CALL_TYPE_GUIDED) {
      this.initializeCallFlow();
      return;
    }

    const flagEnabled = await this.featureFlagsService.isFlagEnabled(
      AppConstants.CERTIFICATION_VIDEO_ACKNOWLEDGEMENT
    );

    if (!flagEnabled) {
      this.initializeCallFlow();
      return;
    }

    this.callStore.setCertAcknowledgementPending(true);
    this.callStore.setHideCameraToggle(true);
    this.initializeCallFlow();
  }

  onAcknowledged(sessionToken: string): void {
    this.showAcknowledgementModal = false;
    if (this.isCameraInactiveInterruption) {
      this.isCameraInactiveInterruption = false;
      this.cameraMonitorCooldownUntil = Date.now() + 5000;
      this.mediaStreamService.cleanupVideoStream();
      this.controlService.unpauseCall();
    } else {
      this.callStore.setAcknowledgementSessionToken(sessionToken);
      this.callStore.setCertAcknowledgementPending(false);
      this.controlService.onStartCallComplete = () => {
        this.playerService.startPlayClips();
      };
      this.controlService.startCall();
    }
  }

  onAcknowledgementCancelled(): void {
    if (this.isCameraInactiveInterruption) {
      this.isCameraInactiveInterruption = false;
      if (this.callStore.getCurrentView() === AppConstants.VIEW_PRACTICE) {
        // During call — same as End button on practice screen (replicates end-call-early behavior)
        this.controlService.resetUserInactivity();
        this.controlService.endCall(true);
        this.controlService.goBackToAssigment();
        this.controlService.resetRecognitionControls();
        this.controlService.isEndCallTriggered = true;
      } else {
        // During briefing/pre-call — same as Leave Briefing button
        this.controlService.goBackToAssigment();
      }
    } else {
      this.callStore.setCertAcknowledgementPending(false);
      this.controlService.goBackToAssigment();
    }
  }


  initializeCallFlow() {
    this.observeNewVideo();
    localStorage.setItem('convoHistory', JSON.stringify({ chatHistory: [] }));
    this.callStore.setIsCallComplete(false);
    this.callStore.setDisableEndCallButton(false);
    this.playerService.shouldHangup = false;
    this.callStore.setCurrentView('');
    this.controlService.getUserInitials();
    this.controlService.isResumeInProgress = false;
    this.controlService.isResumeMessageInProgress = false;

    this.startCameraMonitoringIfRequired();

    if (this.controlService.isRealTimeCall && this.controlService) {
      this.deviceSettingsService?.validateDeviceAccess();
      return;
    }

    // Cert flow: modal will show on pre-call screen — skip startCall, go to device settings directly
    if (this.callStore.getCertAcknowledgementPending()()) {
      this.deviceSettingsService?.validateDeviceAccess();
      this.cdr.detectChanges();
      return;
    }
    let startCallTriggered = false;
    this.callFlagsSubscription =
      this.controlService.isCallTypeResolved$.subscribe((isResolved) => {
        if (isResolved && !startCallTriggered) {
          startCallTriggered = true;
          this.controlService.startCall();
        }
      });
    this.cdr.detectChanges();
  }

  private startCameraMonitoringIfRequired(): void {
    const practiceType = this.callStore.getModuleAssignmentType();
    const callType = this.callStore.getCallType();

    if (practiceType !== AppConstants.TYPE_CERTIFICATION || callType !== AppConstants.CALL_TYPE_GUIDED) {
      return;
    }

    this.featureFlagsService.isFlagEnabled(AppConstants.CERTIFICATION_VIDEO_ACKNOWLEDGEMENT).then(flagEnabled => {
      if (!flagEnabled) return;
      this.startCameraMonitoring();
    });
  }

  private startCameraMonitoring(): void {
    const onCameraInactive = () => {
      if (this.showAcknowledgementModal || this.callStore.getIsPaused()) return;
      if (Date.now() < this.cameraMonitorCooldownUntil) return;
      this.isCameraInactiveInterruption = true;
      this.showAcknowledgementModal = true;
      this.controlService.pauseSimulation();
      this.cdr.detectChanges();
    };

    // Detect browser-level permission revocation (e.g. site settings toggle) — no LED flicker
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: 'camera' as PermissionName })
        .then(status => {
          this.cameraPermissionStatus = status;
          status.onchange = () => {
            if (status.state !== 'granted') onCameraInactive();
          };
        })
        .catch(() => {});
    }

    // Detect physical camera disconnection by checking existing track state — no getUserMedia
    this.certCameraCheckInterval = setInterval(() => {
      if (this.callStore.getIsPaused()) return;
      if (Date.now() < this.cameraMonitorCooldownUntil) return;
      const stream = this.mediaStreamService.getSharedVideoStream();
      if (!stream || !this.mediaStreamService.isVideoStreamActive()) return;
      const tracks = stream.getVideoTracks();
      if (tracks.length === 0 || tracks[0].readyState === 'ended') {
        onCameraInactive();
      }
    }, 1000);
  }

  observeNewVideo(): void {
    // Initialize MutationObserver to detect child elements added
    this.observer = new MutationObserver((mutations) => {
      // disconnect the observer if not on the practice / pre-call view
      const currentView = this.callStore.getCurrentView();
      const isPracticeView = currentView === AppConstants.VIEW_PRACTICE;
      const isPrecallView = currentView === AppConstants.VIEW_PRE_CALL;
      const isDeviceSettingsView =
        currentView === AppConstants.VIEW_DEVICE_SETTINGS;
      const isCall = !isPracticeView && !isPrecallView && !isDeviceSettingsView;
      if (this.observer && isCall) {
        if (currentView === '') {
          return;
        } else {
          this.observer.disconnect();
        }
        return;
      }
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLVideoElement) {
              this.playerService?.addCanPlayFlag(node);
            }
          });
        }
      });
    });

    // Configure and start observing
    this.observer.observe(this.el.nativeElement, {
      childList: true, // Watch for child elements added/removed
      subtree: true, // Watch the entire subtree
    });
  }

  async initCallFlags(callUuid: string) {
    const flags = this.callFlagService.getCallFlagsLS(callUuid);

    this.logger.log('[CALL] flags', { flags });

    if (!flags) {
      this.controlService.goBackToAssigment();
      return;
    }

    this.hasRequiredParams = true;

    this.controlService.isRealTimeCall = !!flags.tavusEnabled;
    this.controlService.pauseEnabled = !!flags.pauseEnabled;
    this.controlService.callThumbnail = flags.callThumbnail;

    const visualAidLocalStorageFlag = !!flags.visualAidEnabled;
    const visualAidFeatureFlag = await this.callFlagService.getVisualAidFeatureFlag();
    this.controlService.isVisualAidCall = visualAidFeatureFlag && visualAidLocalStorageFlag;

    this.callStore.setLanguage(flags.language || '');

    this.controlService.setIsCallTypeResolved(true);
  }

  protected get showLoadingScreen() {
    return (
      this.playerService.showLoadingScreen &&
      !this.callStore.getDisplayPostCall() &&
      this.callStore.getCurrentView() != AppConstants.VIEW_DEVICE_SETTINGS &&
      !this.showPathwayScreen
    );
  }

  protected get showPreCallScreen() {
    return this.callStore.getCurrentView() === AppConstants.VIEW_PRE_CALL;
  }

  protected get showDeviceSettingsScreen() {
    return (
      this.callStore.getCurrentView() === AppConstants.VIEW_DEVICE_SETTINGS
    );
  }

  protected get showPracticeScreen() {
    const isPracticeView =
      this.callStore.getCurrentView() === AppConstants.VIEW_PRACTICE;
    const isConversational =
      this.callType === AppConstants.CALL_TYPE_CONVERSATIONAL;
    const isGuided = this.callType === AppConstants.CALL_TYPE_GUIDED;
    const isRealTime = this.controlService.isRealTimeCall;
    const isVisualAidCall = this.controlService.isVisualAidCall;

    return (
      isPracticeView &&
      (isConversational ||
        (isGuided && !isVisualAidCall) ||
        (isRealTime && !isVisualAidCall && isGuided) ||
        (isRealTime && isVisualAidCall && isGuided))
    );
  }

  protected get showVisualAidScreen() {
    return (
      this.callStore.getCurrentView() === AppConstants.VIEW_PRACTICE &&
      this.controlService.isVisualAidCall &&
      this.callType === AppConstants.CALL_TYPE_GUIDED &&
      !this.controlService.isRealTimeCall
    );
  }

  protected get showPathwayScreen() {
    const practiceType = this.callStore.getModuleAssignmentType();
    return practiceType === 'PATHWAY';
  }

  closeModal() {
    this.showGenderModal = false;
    if (this.hasRequiredParams) {
      this.initializeCallFlow();
    }
  }

  checkIfIsIpad() {
    const isIpad = getDeviceType() === 'ipad';
    this.logger.log('[CALL] is iPad', { isIpad });
    this.callStore.setIsIpad(isIpad);
  }

  detectBrowserBackButton() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event) => {
        const navEvent = event as NavigationStart;
        if (navEvent.navigationTrigger === 'popstate') {
          this.logger.log('[CALL] click browser back button');
          if (this.controlService.isRealTimeCall) {
            this.realtimeService.endRealtimeConversation(true);
          }
        }
      });
  }

}
